import { finbotThemeConfig, type FinbotThemeConfig } from './themes/finbot.config';

// Define the possible theme types
export type ThemeType = 'ueh' | 'finbot';

// Create a union type for all theme configs
export type ThemeConfig = FinbotThemeConfig;

/**
 * Detects the current theme based on environment or build context
 * For finbot frontend, always returns 'finbot'
 */
function detectTheme(): ThemeType {
  // Check environment variable first (backward compatibility)
  const envType = process.env.REACT_APP_TYPE as ThemeType;
  if (envType && (envType === 'ueh' || envType === 'finbot')) {
    return envType;
  }

  // Auto-detect based on build context
  // For Finbot frontend, always default to 'finbot'
  return 'finbot';
}

/**
 * Gets the theme configuration for the current theme
 */
function getThemeConfig(): ThemeConfig {
  const themeType = detectTheme();

  switch (themeType) {
    case 'finbot':
      return finbotThemeConfig;
    case 'ueh':
      // For Finbot frontend, we don't import UEH config
      throw new Error('UEH theme not available in Finbot frontend');
    default:
      return finbotThemeConfig;
  }
}

// Export the current theme configuration
export const currentTheme = getThemeConfig();

/**
 * Helper functions to get specific configuration sections
 */
export const getBrandingConfig = () => currentTheme.branding;
export const getAppearanceConfig = () => currentTheme.appearance;
export const getEmbedConfig = () => currentTheme.embed;
export const getFeaturesConfig = () => currentTheme.features;
export const getApiConfig = () => currentTheme.api;
export const getKeycloakConfig = () => currentTheme.keycloak;

/**
 * Get configuration with environment variable override
 * This allows essential configs to still be overridden by environment variables
 * Note: Finbot currently doesn't use most environment variables
 */
export const getConfigWithEnvOverride = () => {
  const theme = currentTheme;

  return {
    // API URL - can be overridden by environment (not currently used in Finbot)
    apiUrl: process.env.REACT_APP_API_URL || theme.api.defaultUrl,

    // Keycloak config - not used in Finbot (mock auth)
    keycloak: {
      realm: theme.keycloak.defaultRealm,
      clientId: theme.keycloak.defaultClientId
    },

    // Theme-based configs (not overrideable by environment)
    branding: theme.branding,
    appearance: theme.appearance,
    embed: theme.embed,
    features: theme.features
  };
};

export default currentTheme;
